import React, { FC, ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import GSApi from 'api/Api';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { useIsWithinChannelReplayDepth } from 'hooks/useIsWithinChannelReplayDepth/useIsWithinChannelReplayDepth';
import { useTVAssetEntitlementState } from 'hooks/useTVAssetEntitlementState/useTVAssetEntitlementState';
import { FetchParamById } from 'types/ApiTypes';
import { Episode, SeriesApiResponse, SeriesEpisodesApiResponse, TVSeriesDetailEpisode } from 'types/Asset';
import { TVAssetEntitlementState } from 'types/Entitlement';
import { BroadcastPlayerAsset, PlayingAsset, PlayingMode } from 'types/Player';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useChannels } from 'providers/useChannels/useChannels';
import { usePlayer } from 'providers/player/PlayerContext';
import { useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { useUpsellFlow } from 'providers/useUpsellFlow/UpsellFlowContext';
import { useVodWatchable } from 'hooks/useVodWatchable';
import { ControlGroup } from 'components/Player/PlayerWrapper';
import { isEpisodeTrailer } from 'utils/fnData';
import { isFuture, isNow, isPast, getStartEndTimes } from 'utils/fnDate';
import { seriesDetailsParser } from 'utils/fnParser';
import { openFlowAfterNavigation } from 'utils/fnPurchaseFlow';
import { isVodPlayerAsset } from 'utils/fnTypeGuards';
import { getBroadcastDetailsURL, getCatchupPlayerURL, getLivePlayerURL, getSeriesDetailsUrl, getVodPlayerURL } from 'utils/fnUrl';
import { useApp } from 'providers/useApp/AppContext';

type EpisodePlaybackTarget = { url: string; isLive: boolean; isVOD: boolean; channelId?: string };

export type EpisodeState = {
    isPlayable: boolean;
    requiresSubscription: boolean;
    isUpcoming: boolean;
};

const isSameChannel = (episode: TVSeriesDetailEpisode, channelId: string) =>
    !channelId || String(episode?.listings?.[0]?.channelId) === String(channelId);

const isPlayingEpisode = (episode: TVSeriesDetailEpisode, asset: PlayingAsset, isVOD: boolean) => {
    if (isVOD) {
        return !!asset?.id && String(episode?.id) === String(asset.id);
    }

    const listingId = (asset as BroadcastPlayerAsset)?.listingId;

    return !!listingId && episode?.listings?.some((listing) => String(listing?.id) === String(listingId));
};

const getPlaybackTarget = (episode: TVSeriesDetailEpisode, isVOD: boolean): EpisodePlaybackTarget => {
    if (isVOD) {
        return episode?.id ? { url: getVodPlayerURL(episode.id), isLive: false, isVOD: true } : null;
    }

    const listing = episode?.listings?.[0];

    if (!listing?.start || !listing?.duration) {
        return null;
    }

    const { startTime, endTime } = getStartEndTimes(listing.start, listing.duration);

    if (isNow(startTime, endTime)) {
        return { url: getLivePlayerURL(listing.channelId), isLive: true, isVOD: false, channelId: listing.channelId };
    }

    if (isPast(endTime)) {
        return { url: getCatchupPlayerURL(listing.id), isLive: false, isVOD: false, channelId: listing.channelId };
    }

    return null;
};

const usePlayerSeriesEpisodesService = () => {
    const navigate = useNavigate();
    const { isGuest } = useAuth();
    const { appLanguage } = useApp();
    const { initSubscribeFlow } = useSubscription();
    const { initUpsellFlow } = useUpsellFlow();
    const getEntitlementState = useTVAssetEntitlementState();
    const checkIsWithinChannelReplayDepth = useIsWithinChannelReplayDepth();
    const { getChannelById } = useChannels();
    const { asset, setActiveControlGroup, setPlayingMode } = usePlayer();

    const [selectedSeason, setSelectedSeason] = useState<number>(null);

    const isVOD = !!isVodPlayerAsset(asset);
    const { isVodEpisodeWatchable } = useVodWatchable(isVOD);
    const seriesId = isVOD ? asset?.vodSeriesId : (asset as BroadcastPlayerAsset)?.seriesId;
    const channelId = isVOD ? null : ((asset as BroadcastPlayerAsset)?.channel_id ?? (asset as BroadcastPlayerAsset)?.channelId);

    const { response: seriesResponse, fetcher: fetchSeriesDetails } = useDataFetcher<SeriesApiResponse, FetchParamById>((param) =>
        isVOD ? GSApi.fetchVodSeriesDetails(param.id, (data) => data) : GSApi.fetchProgramSeriesDetails(param.id, (data) => data)
    );

    const {
        response: episodesResponse,
        loading,
        fetcher: fetchSeriesEpisodes,
    } = useDataFetcher<SeriesEpisodesApiResponse, FetchParamById>((param) =>
        isVOD ? GSApi.fetchVodSeriesEpisodes(param.id, (data) => data) : GSApi.fetchProgramSeriesEpisodes(param.id, (data) => data)
    );

    useEffect(() => {
        if (!seriesId) {
            return;
        }

        setSelectedSeason(null);
        fetchSeriesDetails({ id: seriesId }, true);
        fetchSeriesEpisodes({ id: seriesId }, true);
    }, [seriesId]);

    const episodeList = useMemo(() => {
        if (!seriesResponse || !episodesResponse) {
            return [];
        }

        const { parsedEpisodes } = seriesDetailsParser(seriesResponse, episodesResponse, isVOD);

        return parsedEpisodes.filter((episode) => !isEpisodeTrailer(episode)).filter((episode) => isSameChannel(episode, channelId));
    }, [seriesResponse, episodesResponse, isVOD, channelId]);

    const seasons = useMemo(
        () =>
            episodeList.reduce((unique: number[], episode) => {
                if (episode?.season_number == null || unique.includes(episode.season_number)) {
                    return unique;
                }

                return [...unique, episode.season_number];
            }, []),
        [episodeList]
    );

    const playingEpisode = useMemo(
        () => episodeList.find((episode) => isPlayingEpisode(episode, asset, isVOD)),
        [episodeList, asset, isVOD]
    );

    const activeSeason = selectedSeason ?? playingEpisode?.season_number ?? seasons[0] ?? null;

    const episodes = useMemo(() => episodeList.filter((episode) => episode.season_number === activeSeason), [episodeList, activeSeason]);

    const nextEpisode = useMemo(() => {
        if (!playingEpisode) {
            return null;
        }

        const sameSeasonNext = episodeList.find(
            (episode) =>
                episode.season_number === playingEpisode.season_number && episode.episode_number === playingEpisode.episode_number + 1
        );

        const nextSeasonFirst = episodeList
            .filter((episode) => episode.season_number === playingEpisode.season_number + 1)
            .sort((a, b) => a.episode_number - b.episode_number)[0];

        const candidate = sameSeasonNext ?? nextSeasonFirst;

        return candidate && getPlaybackTarget(candidate, isVOD) ? candidate : null;
    }, [episodeList, playingEpisode, isVOD]);

    const getBroadcastEntitlementState = (episode: TVSeriesDetailEpisode, target: EpisodePlaybackTarget): TVAssetEntitlementState => {
        const listing = episode.listings[0];
        const channel = getChannelById(listing.channelId);
        const channelEntitlement = getEntitlementState({
            channel_id: listing.channelId,
            additional_services_available: listing.additional_services_available,
        });

        const isCatchup = !!target && !target.isLive;

        if (
            !channel ||
            channelEntitlement !== TVAssetEntitlementState.ENTITLED ||
            !isCatchup ||
            isPlayingEpisode(episode, asset, false) ||
            checkIsWithinChannelReplayDepth({ channel_id: listing.channelId, start: listing.start })
        ) {
            return channelEntitlement;
        }

        return channel.replay
            ? TVAssetEntitlementState.SHOULD_BUY_EXTENDED_REPLAY_WINDOW
            : TVAssetEntitlementState.SHOULD_BUY_BASIC_REPLAY_WINDOW;
    };

    const getEpisodeState = (episode: TVSeriesDetailEpisode): EpisodeState => {
        const listing = episode?.listings?.[0];
        const target = getPlaybackTarget(episode, isVOD);
        const isPlayable = !!target;
        const isUpcoming = !isVOD && !!listing?.start && isFuture(listing.start * 1000);

        if (isVOD) {
            const requiresSubscription = !isGuest && !isPlayingEpisode(episode, asset, isVOD) && !isVodEpisodeWatchable(episode);

            return { isPlayable, requiresSubscription, isUpcoming };
        }

        if (isGuest || !listing) {
            return { isPlayable, requiresSubscription: false, isUpcoming };
        }

        return {
            isPlayable,
            requiresSubscription: getBroadcastEntitlementState(episode, target) !== TVAssetEntitlementState.ENTITLED,
            isUpcoming,
        };
    };

    const closePlayerAndOpenUpsell = (detailsUrl: string, openUpsell: () => void) => {
        if (!detailsUrl) {
            return;
        }

        openFlowAfterNavigation(openUpsell);
        navigate(detailsUrl, { state: { closePlayer: true } });
    };

    const openEpisodeDetails = (episode: TVSeriesDetailEpisode) => {
        const detailsUrl = getBroadcastDetailsURL(episode, {}, appLanguage);

        if (!detailsUrl) {
            return;
        }

        setActiveControlGroup(ControlGroup.BASE);
        navigate(detailsUrl);
    };

    const playEpisode = (episode: TVSeriesDetailEpisode) => {
        const target = getPlaybackTarget(episode, isVOD);

        if (!target) {
            return;
        }

        setActiveControlGroup(ControlGroup.BASE);

        if (getEpisodeState(episode).requiresSubscription) {
            if (target.isVOD) {
                closePlayerAndOpenUpsell(getSeriesDetailsUrl(seriesId, true, null, appLanguage), () =>
                    initUpsellFlow(target.url, episode as unknown as Episode)
                );
                return;
            }

            if (getBroadcastEntitlementState(episode, target) !== TVAssetEntitlementState.SHOULD_SUBSCRIBE_CHANNEL) {
                closePlayerAndOpenUpsell(getBroadcastDetailsURL(episode, {}, appLanguage), () =>
                    initSubscribeFlow(target.url, { type: 'Listing', code: episode.listings[0].id })
                );
                return;
            }

            initSubscribeFlow(target.url, { type: 'Channel', code: target.channelId });
            return;
        }

        if (target.isLive) {
            setPlayingMode(PlayingMode.NORMAL);
            navigate(target.url, { replace: true, state: { channelSwitch: true } });
            return;
        }

        navigate(target.url, { replace: true });
    };

    return {
        seasons,
        episodes,
        activeSeason,
        selectSeason: setSelectedSeason,
        isLoading: loading,
        isVOD,
        playingEpisodeId: playingEpisode?.id,
        nextEpisode,
        getEpisodeState,
        playEpisode,
        openEpisodeDetails,
    };
};

const PlayerSeriesEpisodesContext = createContext<ReturnType<typeof usePlayerSeriesEpisodesService>>(null);

export const PlayerSeriesEpisodesProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const service = usePlayerSeriesEpisodesService();

    return <PlayerSeriesEpisodesContext.Provider value={service}>{children}</PlayerSeriesEpisodesContext.Provider>;
};

export const usePlayerSeriesEpisodes = () => useContext(PlayerSeriesEpisodesContext);
